import React from "react";

/**
 * Filet pour les morceaux de code différés introuvables (App.tsx).
 *
 * Le dossier `build/` est remplacé à chaque déploiement, et les noms de
 * fichiers contiennent un hash. Un onglet resté ouvert avant un
 * déploiement demande donc un morceau qui n'existe plus : React lève une
 * `ChunkLoadError` et l'écran se vide — exactement le symptôme d'« écran
 * gris » déjà vécu deux fois pour une autre raison.
 *
 * On recharge alors la page une seule fois : elle repart sur le nouveau
 * build. Le drapeau en `sessionStorage` évite la boucle de rechargement si
 * l'échec vient d'autre chose (réseau coupé, par exemple).
 */
const CLE_RECHARGEMENT = "lc_rechargement_apres_chunk";

type Props = { children: React.ReactNode };
type State = { enEchec: boolean };

export default class ChunkErrorBoundary extends React.Component<Props, State> {
    state: State = { enEchec: false };

    static getDerivedStateFromError(): State {
        return { enEchec: true };
    }

    componentDidCatch(erreur: Error) {
        const estChunk =
            erreur?.name === "ChunkLoadError" ||
            /Loading chunk .* failed/i.test(erreur?.message ?? "");

        if (!estChunk) {
            throw erreur; // autre bogue : ne pas le masquer derrière un rechargement
        }

        if (sessionStorage.getItem(CLE_RECHARGEMENT)) {
            return; // déjà tenté : on affiche le message plutôt que de boucler
        }

        sessionStorage.setItem(CLE_RECHARGEMENT, "1");
        window.location.reload();
    }

    componentDidMount() {
        // Chargement réussi : on oublie la tentative précédente.
        sessionStorage.removeItem(CLE_RECHARGEMENT);
    }

    render() {
        if (this.state.enEchec) {
            return (
                <div style={{ padding: "40px", textAlign: "center", fontFamily: "'Trebuchet MS', sans-serif" }}>
                    <p>Cette page a été mise à jour.</p>
                    <button onClick={() => window.location.reload()}>Recharger</button>
                </div>
            );
        }

        return this.props.children;
    }
}
